import {UNIT_TYPES} from './units';

/**
 * Найм отряда и световая волна по ячейке.
 *
 * ВОЛНА — шестиугольник света цветом игрока: плотный фронт, длинный шлейф за ним и
 * светлая линия фронта с бликами в вершинах (текстуры — utils/effects/hexWaveTextures).
 * Ею сопровождаются найм, докупка размера и бой.
 *
 * Волна лежит ПОВЕРХ всего поля — иначе ее перекрывают маркеры и кнопки соседних
 * ячеек, — а «из-под казармы» она выходит за счет маски по своему гексу: внутри
 * ячейки волна не рисуется вовсе (см. HexGridRenderer.showUnitRecruitmentPulse).
 *
 * НАЙМ — отряд проявляется из света на казарме (см. UNIT_RECRUITMENT_CAST).
 */

export const HEX_WAVE_TEXTURE_KEYS = {
  // Тело волны: фронт и шлейф, красится в цвет игрока
  BAND: 'hex_wave_band',
  // Линия фронта: красится в светлый оттенок того же цвета
  CORE: 'hex_wave_core',
  // Столб света найма
  BEAM: 'light_beam',
};

export const RECRUITMENT_PULSE = {
  textureKey: HEX_WAVE_TEXTURE_KEYS.BAND,
  coreTextureKey: HEX_WAVE_TEXTURE_KEYS.CORE,
  // Насколько линия фронта светлее цвета игрока: иначе темные цвета не светятся
  coreLightShare: 0.68,
  // Быстро, но плавно: волна успевает разойтись, пока отряд встает на поле
  durationMs: 460,
  // Радиус фронта в долях радиуса ячейки: стартует внутри нее и уходит за пределы
  startScaleFactor: 0.8,
  endScaleFactor: 2.2,
  // Видна сразу и во весь цвет: затухать начинает только с середины пути,
  // иначе волна почти не читается
  startAlpha: 1,
  // С какой доли пути волна начинает гаснуть
  alphaFadeStartShare: 0.5,
  // Следующая волна идет почти вплотную за первой
  repeatDelayShare: 0.25,
};

/**
 * Волна по ячейке в такт боевым иконкам: та же волна, что у докупки размера отряду,
 * но своего размаха — бой идет прямо под ней, и волна во всю ширину закрывала бы
 * соседние отряды. Глубину боевой волны задает LAYER_DEPTH.COMBAT_CELL_PULSE (ниже
 * отрядов), у найма она своя
 */
export const COMBAT_CELL_PULSE = {
  // Во сколько раз меньше волны найма расходится боевая
  endScaleDivider: 1.5,
  // Во сколько раз дольше идет боевая волна: у найма это вспышка на спокойном
  // поле, а в бою она должна читаться на фоне мигающих иконок и цифр урона
  durationMultiplier: 2,
  // Как часто волна повторяется, мс. Раньше ритм задавало мигание иконки
  // (600 разгорание + 600 пауза + 600 затухание); мигание выключили
  // (COMBAT_INDICATOR_FADING_ENABLED), и период стал своим
  intervalMs: 1800,
};

/** Волн на докупку размера отряду. У найма их число зависит от типа отряда */
export const RECRUITMENT_PULSE_COUNT = {
  SIZE: 1,
};

/**
 * АНИМАЦИЯ НАЙМА: на казарме разгорается свет цвета игрока, к ячейке сходятся
 * шестиугольные кольца и искры, встает столб света, и в нем из белого силуэта
 * проявляется отряд. Момент проявления (impactMs) — вспышка, звук найма, монеты и
 * волна; настоящий отряд встает на поле под двойником и сменяет его незаметно.
 *
 * Чем круче тип, тем длиннее разгон и тем больше слоев (порядок силы: d6, d10, d8,
 * d4, d12, d20). Новое по сравнению с младшим типом:
 *  - d6 — столб, кольцо, искры;
 *  - d10 — выше столб, второе кольцо, разлет искр, тихий волшебный звук на разгоне;
 *  - d8 — лучи за отрядом, золото в искрах, звук разгона громче;
 *  - d4 — вращающийся знак на земле, восходящий поток, третья волна;
 *  - d12 — поле темнеет, сверху бьет разряд, камера вздрагивает;
 *  - d20 — поле темнеет сильнее, два знака, золотой венец над отрядом, дождь искр
 *    и сильный удар.
 *
 * Значения — по порядку типов. Время в миллисекундах, размеры — доли размера ячейки.
 */
const TYPES_BY_POWER = [UNIT_TYPES.D6, UNIT_TYPES.D10, UNIT_TYPES.D8, UNIT_TYPES.D4, UNIT_TYPES.D12, UNIT_TYPES.D20];

export const UNIT_RECRUITMENT_CAST = {
  // Общие параметры всех типов
  aura: {growFrom: 0.45},
  // Затемнение поля: большое мягкое пятно; долю хвоста держится, потом уходит
  dim: {sizeFactor: 30, holdShare: 0.35},
  // Кольца: живут такую долю разгона и сходятся до такого радиуса
  chargeRing: {lifeShare: 0.62, toFactor: 0.55},
  ringCoreAlpha: 0.95,
  ringGlowAlpha: 0.6,
  // Шар, собирающийся в центре к моменту проявления
  gather: {fromShare: 0.4, sizeFactor: 1.15, alpha: 0.95},
  beam: {appearShare: 0.3, coreWidthShare: 0.42, flareWidth: 1.45},
  unit: {fromScale: 0.4, riseFactor: 0.32, fadeOutShare: 0.25},
  bloom: {sizeFactor: 2.1, durationMs: 300, alpha: 0.95},
  flash: {leadMs: 30, durationMs: 280},
  impactRings: {gapMs: 90, durationMs: 620, alpha: 0.95},
  burst: {lifeMs: 720},
  stream: {
    lifeMs: 650,
    minSizeFactor: 0.15,
    maxSizeFactor: 0.32,
    startRadiusFactor: 0.8,
    endRadiusFactor: 0.25,
    turns: 0.8,
    swirlFlatten: 0.3,
    topJitterFactor: 0.4,
    topLiftFactor: 1.5,
  },
  motes: {sizeFactor: 0.24, turn: 1.15, lifeShare: 0.62},
  rays: {leadMs: 60, turn: 0.75, lifeMs: 900},
  rune: {radiusFactor: 1.08, innerRadiusFactor: 0.72, turn: 1.1, alpha: 0.95},
  strike: {leadMs: 110, holdMs: 150, widthFactor: 0.62},
  crown: {liftFactor: 0.78, radiusFactor: 0.72, flatten: 0.3, count: 10, sizeFactor: 0.3, turn: 1.6, appearMs: 160},
  rain: {spreadFactor: 2.1, topLiftFactor: 2.3, fallFactor: 3.2, sizeFactor: 0.2, spawnMs: 800, lifeMs: 720},

  // По типам, от младшего к старшему
  impactMs: [440, 500, 580, 680, 820, 1060],
  tailMs: [640, 740, 860, 1020, 1260, 1720],
  auraSizeFactor: [2.0, 2.15, 2.4, 2.7, 3.1, 3.8],
  auraAlpha: [0.82, 0.86, 0.9, 0.94, 0.98, 1],
  // Кольца, сходящиеся к ячейке: сколько и с какого радиуса
  ringCount: [1, 2, 2, 3, 3, 4],
  ringFromFactor: [1.5, 1.65, 1.8, 2, 2.35, 2.8],
  beamWidthFactor: [0.85, 0.95, 1.1, 1.25, 1.5, 1.85],
  beamHeightFactor: [2.1, 2.45, 2.85, 3.3, 4.1, 5.4],
  beamTailMs: [320, 380, 450, 540, 680, 900],
  moteCount: [6, 9, 12, 16, 22, 32],
  unitMaterializeMs: [220, 240, 280, 320, 380, 480],
  unitOvershoot: [1.14, 1.17, 1.2, 1.24, 1.3, 1.38],
  unitSettleMs: [170, 180, 200, 220, 260, 320],
  flashSizeFactor: [1.8, 2, 2.3, 2.7, 3.3, 4.3],
  impactRingCount: [1, 1, 2, 2, 3, 3],
  impactRingSizeFactor: [2.5, 2.7, 3, 3.4, 4, 5],
  burstCount: [6, 9, 12, 16, 22, 32],
  burstSpreadFactor: [1.05, 1.2, 1.35, 1.55, 1.85, 2.35],
  burstSizeFactor: [0.22, 0.24, 0.27, 0.3, 0.33, 0.38],
  // Какая доля искр и колец золотая: младшие светятся только цветом игрока
  goldShare: [0, 0, 0.3, 0.5, 0.62, 0.8],
  raysSizeFactor: [0, 0, 2.5, 2.9, 3.6, 4.8],
  raysAlpha: [0, 0, 0.75, 0.85, 0.92, 1],
  raysLayers: [0, 0, 1, 1, 2, 2],
  streamCount: [0, 0, 0, 16, 26, 44],
  runeCount: [0, 0, 0, 1, 1, 2],
  strikeCount: [0, 0, 0, 0, 1, 1],
  strikeHeightFactor: [0, 0, 0, 0, 6, 9],
  dimAlpha: [0, 0, 0, 0, 0.42, 0.72],
  crownCount: [0, 0, 0, 0, 0, 1],
  rainCount: [0, 0, 0, 0, 0, 26],
  shakeMs: [0, 0, 0, 0, 170, 340],
  shakeIntensity: [0, 0, 0, 0, 0.0024, 0.0062],
  // Волна на проявлении
  waveCount: [2, 2, 2, 3, 3, 4],
  waveEndFactor: [2, 2.2, 2.5, 2.85, 3.4, 4.4],
  waveDurationMs: [460, 490, 530, 580, 660, 800],
  // Волшебный подъем под разгон: тишина только у простой пехоты d6. Удар на проявлении —
  // только у самых старших
  castSoundLevel: [0, 0.26, 0.4, 0.55, 0.75, 1],
  impactSoundLevel: [0, 0, 0, 0, 0.5, 0.85],
};

export const UNIT_RECRUITMENT_TIER_COUNT = TYPES_BY_POWER.length;

/**
 * Номер силы типа отряда: 0 — d6 … 5 — d20. Неизвестный тип — младший
 * @param {string} unitType — id типа (unit_d6)
 * @returns {number}
 */
export const resolveUnitRecruitmentTier = (unitType) => Math.max(
  0, TYPES_BY_POWER.findIndex((type) => type.id === unitType),
);

const TIER_ARRAY_KEYS = Object.keys(UNIT_RECRUITMENT_CAST).filter((key) => Array.isArray(UNIT_RECRUITMENT_CAST[key]));

/**
 * Параметры анимации для типа отряда: общие плюс значения его столбца
 * @param {string} unitType
 * @returns {Object}
 */
export const resolveUnitRecruitmentCast = (unitType) => {
  const tier = resolveUnitRecruitmentTier(unitType);
  const cast = {...UNIT_RECRUITMENT_CAST, tier};
  TIER_ARRAY_KEYS.forEach((key) => {
    cast[key] = UNIT_RECRUITMENT_CAST[key][tier];
  });
  return cast;
};
